import type {
  HelpExecutorAdminDto,
  HelpOrderEventDto,
  HelpOrderItemDto,
  HelpOrderItemPatchDto,
  HelpPointDto,
  HelpServiceAdminDto,
  SuperadminHelpOrderDetailDto,
  UpdateHelpOrderDto,
} from "@pro-sto/api-client";
import type {
  HelpEvent,
  HelpExecutor,
  HelpExecutorProfile,
  HelpLineItem,
  HelpOrderDetail,
  HelpPaymentStatus,
  HelpPoint,
  HelpPricingMode,
  HelpProvider,
  HelpService,
  HelpServiceKind,
} from "./types";

const HELP_KINDS: HelpServiceKind[] = ["tow", "fuel", "jump_start"];
const HELP_PAYMENTS: HelpPaymentStatus[] = ["pending", "success", "refunded"];
const HELP_PRICING: HelpPricingMode[] = ["fixed", "per_unit", "flexible"];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

export function unwrapHelpPayload(raw: unknown): unknown {
  const record = asRecord(raw);
  if (!record) return raw;
  if (typeof record["uuid"] === "string") return record;
  const nested = record["data"] ?? record["order"] ?? record["result"];
  const nestedRecord = asRecord(nested);
  if (nestedRecord && typeof nestedRecord["uuid"] === "string") return nestedRecord;
  return Array.isArray(nested) ? nested : raw;
}

export function unwrapHelpList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const record = asRecord(raw);
  if (!record) return [];
  const nested = record["data"] ?? record["items"] ?? record["results"];
  return Array.isArray(nested) ? nested : [];
}

function pickString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return "";
}

function pickNullableString(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
    if (value === null) return null;
  }
  return null;
}

function pickNumber(record: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return 0;
}

function pickNullableNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function asKind(value: string): HelpServiceKind {
  return HELP_KINDS.includes(value as HelpServiceKind)
    ? (value as HelpServiceKind)
    : "tow";
}

function asPayment(value: string): HelpPaymentStatus {
  return HELP_PAYMENTS.includes(value as HelpPaymentStatus)
    ? (value as HelpPaymentStatus)
    : "pending";
}

function asStatus(value: string): SuperadminHelpOrderDetailDto["status"] | "" {
  const allowed = [
    "awaiting_confirmation",
    "en_route",
    "on_site",
    "in_progress",
    "completed",
    "cancelled",
  ] as const;
  return allowed.includes(value as (typeof allowed)[number])
    ? (value as SuperadminHelpOrderDetailDto["status"])
    : "";
}

function asProvider(value: string): HelpProvider {
  return value === "lat" ? "lat" : "internal";
}

function asPricing(value: string): HelpPricingMode {
  return HELP_PRICING.includes(value as HelpPricingMode)
    ? (value as HelpPricingMode)
    : "fixed";
}

function toIso(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return "";
}

function emptyPoint(): HelpPoint {
  return { city: "", address: "", lat: 0, lon: 0, note: "" };
}

function pickCoord(
  record: Record<string, unknown>,
  ...keys: string[]
): number | null {
  const value = pickNullableNumber(record, ...keys);
  return value == null ? null : value;
}

function normalizeLatLon(lat: number, lon: number): { lat: number; lon: number } {
  if (Math.abs(lat) > 90 && Math.abs(lon) <= 90) {
    return { lat: lon, lon: lat };
  }
  return { lat, lon };
}

function coordsFromRecord(record: Record<string, unknown>): { lat: number; lon: number } {
  const nested =
    asRecord(record["location"]) ??
    asRecord(record["geo"]) ??
    asRecord(record["point"]) ??
    asRecord(record["coordinates"]);

  let lat =
    pickCoord(record, "lat", "latitude") ??
    (nested ? pickCoord(nested, "lat", "latitude") : null);
  let lon =
    pickCoord(record, "lon", "lng", "longitude") ??
    (nested ? pickCoord(nested, "lon", "lng", "longitude") : null);

  const pair = record["coordinates"];
  if ((lat == null || lon == null) && Array.isArray(pair) && pair.length >= 2) {
    const first = Number(pair[0]);
    const second = Number(pair[1]);
    if (Number.isFinite(first) && Number.isFinite(second)) {
      // GeoJSON: [lon, lat]
      if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
        lat = second;
        lon = first;
      } else {
        lat = first;
        lon = second;
      }
    }
  }

  return normalizeLatLon(lat ?? 0, lon ?? 0);
}

export function mapPoint(raw: HelpPointDto | null | undefined): HelpPoint | null {
  if (!raw) return null;
  const record = asRecord(raw) ?? {};
  const { lat, lon } = coordsFromRecord(record);
  return {
    city: pickString(record, "city"),
    address: pickString(record, "address"),
    lat,
    lon,
    note: pickString(record, "note"),
  };
}

function mapPointRequired(raw: HelpPointDto | null | undefined): HelpPoint {
  return mapPoint(raw) ?? emptyPoint();
}

function mapExecutor(raw: unknown): HelpExecutor | null {
  const record = asRecord(raw);
  if (!record) return null;
  const name = pickString(record, "name");
  if (!name.trim()) return null;
  const catalogUuid = pickNullableString(record, "uuid", "catalog_uuid", "catalogUuid");
  return {
    source: catalogUuid ? "catalog" : "custom",
    catalogUuid,
    name,
    phone: pickString(record, "phone"),
    vehicleModel: pickString(record, "vehicle_model", "vehicleModel"),
    vehiclePlate: pickString(record, "vehicle_plate", "vehiclePlate"),
    experienceYears: pickNumber(record, "experience_years", "experienceYears"),
    company: pickString(record, "company"),
    lat: pickNullableNumber(record, "lat"),
    lon: pickNullableNumber(record, "lon", "lng", "longitude"),
    etaMinutes: pickNullableNumber(record, "eta_minutes", "etaMinutes"),
  };
}

function mapItem(raw: HelpOrderItemDto | unknown, index = 0): HelpLineItem | null {
  const record = asRecord(raw);
  if (!record) return null;
  const title = pickString(record, "title", "name");
  if (!title) return null;
  const id = pickString(record, "uuid", "id") || `item-${index}-${title}`;
  const serviceUuid = pickNullableString(record, "service_uuid", "serviceUuid");
  return {
    id,
    source: serviceUuid ? "catalog" : "custom",
    serviceUuid,
    code: pickString(record, "code"),
    title,
    quantity: pickNumber(record, "quantity") || 1,
    unitPrice: pickNumber(record, "unit_price", "unitPrice", "price"),
    pricingMode: asPricing(pickString(record, "pricing_mode", "pricingMode")),
  };
}

function mapEvent(raw: HelpOrderEventDto | unknown, index = 0): HelpEvent | null {
  const record = asRecord(raw);
  if (!record) return null;
  const type = pickString(record, "type");
  const eventType: HelpEvent["type"] =
    type === "status" ||
    type === "comment" ||
    type === "notify" ||
    type === "executor"
      ? type
      : "system";
  const title =
    pickString(record, "title") ||
    (eventType === "comment"
      ? "Комментарий"
      : eventType === "notify"
        ? "Уведомление клиенту"
        : eventType === "status"
          ? "Статус"
          : "Событие");
  const id = pickString(record, "uuid", "id") || `event-${index}-${title}`;
  const rawDetail = record["detail"] ?? record["text"] ?? record["message"];
  const detail =
    typeof rawDetail === "string"
      ? rawDetail
      : pickNullableString(asRecord(rawDetail) ?? {}, "text", "detail", "message");
  return {
    id,
    type: eventType,
    at: toIso(record["created_at"] ?? record["at"] ?? record["createdAt"]),
    title,
    detail: detail || undefined,
  };
}

function mapComment(raw: unknown): string {
  if (typeof raw === "string") return raw;
  const record = asRecord(raw);
  if (!record) return "";
  return pickString(record, "text", "comment", "value", "message");
}

function serviceTitleFromItems(
  items: HelpLineItem[],
  kind: HelpServiceKind,
  fallback: string,
): string {
  if (fallback) return fallback;
  const main = items.find(
    (item) => item.code !== "hard_access" && item.code !== "blocked_wheels",
  );
  return main?.title || kind;
}

export function mapHelpOrderDetail(raw: unknown): HelpOrderDetail | null {
  const unwrapped = unwrapHelpPayload(raw);
  const record = asRecord(unwrapped);
  if (!record) return null;
  const uuid = pickString(record, "uuid");
  if (!uuid) return null;

  const kind = asKind(pickString(record, "kind"));
  const items = Array.isArray(record["items"])
    ? record["items"]
        .map((item, index) => mapItem(item, index))
        .filter((item): item is HelpLineItem => item !== null)
    : [];
  const events = Array.isArray(record["events"])
    ? record["events"]
        .map((event, index) => mapEvent(event, index))
        .filter((event): event is HelpEvent => event !== null)
    : [];

  const dto = unwrapped as SuperadminHelpOrderDetailDto;
  const comment = mapComment(record["comment"]);

  return {
    uuid,
    createdAt: toIso(record["created_at"] ?? record["createdAt"] ?? dto.created_at),
    updatedAt: toIso(record["updated_at"] ?? record["updatedAt"] ?? dto.updated_at),
    status:
      asStatus(pickString(record, "status") || dto.status) || "awaiting_confirmation",
    kind,
    serviceTitle: serviceTitleFromItems(
      items,
      kind,
      pickString(record, "service_title", "serviceTitle"),
    ),
    clientName: pickString(record, "client_name", "clientName") || dto.client_name,
    clientPhone:
      pickString(record, "client_phone", "clientPhone") || dto.client_phone,
    clientUuid: pickString(record, "client_uuid", "clientUuid") || dto.client_uuid,
    carUuid: pickNullableString(record, "car_uuid", "carUuid"),
    vehicle: {
      makeModel:
        pickString(record, "vehicle_make_model", "vehicleMakeModel") ||
        dto.vehicle_make_model,
      regNumber:
        pickString(record, "vehicle_reg_number", "vehicleRegNumber") ||
        dto.vehicle_reg_number,
      vin: pickNullableString(record, "vehicle_vin", "vehicleVin"),
      year: pickNullableString(record, "vehicle_year", "vehicleYear"),
    },
    pickup: mapPointRequired(dto.pickup ?? (record["pickup"] as HelpPointDto)),
    dropoff: mapPoint(
      (dto.dropoff as HelpPointDto | null | undefined) ??
        (record["dropoff"] as HelpPointDto | null),
    ),
    comment,
    blockedWheels: pickNumber(record, "blocked_wheels", "blockedWheels"),
    hardToAccess: Boolean(record["hard_to_access"] ?? record["hardToAccess"]),
    items,
    prepaidAmount: pickNumber(record, "prepaid_amount", "prepaidAmount"),
    prepaidStatus: asPayment(
      pickString(record, "prepaid_status", "prepaidStatus"),
    ),
    extraAmount: pickNumber(record, "extra_amount", "extraAmount"),
    extraPaymentStatus: asPayment(
      pickString(record, "extra_payment_status", "extraPaymentStatus"),
    ),
    totalAmount: pickNumber(record, "total_amount", "totalAmount"),
    provider: asProvider(pickString(record, "provider") || "internal"),
    latOrderId: pickNullableString(record, "lat_order_id", "latOrderId"),
    latGuid: pickNullableString(record, "lat_guid", "latGuid"),
    latShortLink: pickNullableString(record, "lat_short_link", "latShortLink"),
    latStatusRaw: pickNullableString(record, "lat_status_raw", "latStatusRaw"),
    latAmount: pickNullableNumber(record, "lat_amount", "latAmount"),
    latTest: Boolean(record["lat_test"] ?? record["latTest"] ?? true),
    latSentAt: toIso(record["lat_sent_at"] ?? record["latSentAt"]) || null,
    latSyncedAt: toIso(record["lat_synced_at"] ?? record["latSyncedAt"]) || null,
    latLastError: pickNullableString(record, "lat_last_error", "latLastError"),
    executor: mapExecutor(record["executor"]),
    events,
  };
}

export function mapHelpService(raw: HelpServiceAdminDto | unknown): HelpService {
  const record = asRecord(raw) ?? {};
  const dto = raw as HelpServiceAdminDto;
  return {
    uuid: pickString(record, "uuid") || dto.uuid,
    kind: (pickString(record, "kind") || dto.kind) as HelpService["kind"],
    code: pickString(record, "code") || dto.code,
    title: pickString(record, "title") || dto.title,
    description: pickString(record, "description") || dto.description || "",
    unitPrice: pickNumber(record, "unit_price", "unitPrice") || dto.unit_price,
    pricingMode: asPricing(pickString(record, "pricing_mode", "pricingMode") || dto.pricing_mode),
    unitLabel: pickString(record, "unit_label", "unitLabel") || dto.unit_label || "",
    enabled: Boolean(record["enabled"] ?? dto.enabled),
    comingSoon: Boolean(record["coming_soon"] ?? record["comingSoon"] ?? dto.coming_soon),
    providerServiceId: pickString(
      record,
      "provider_service_id",
      "providerServiceId",
    ),
  };
}

export function mapHelpExecutorProfile(
  raw: HelpExecutorAdminDto | unknown,
): HelpExecutorProfile {
  const record = asRecord(raw) ?? {};
  const dto = raw as HelpExecutorAdminDto;
  return {
    uuid: pickString(record, "uuid") || dto.uuid,
    name: pickString(record, "name") || dto.name,
    phone: pickString(record, "phone") || dto.phone,
    vehicleModel:
      pickString(record, "vehicle_model", "vehicleModel") || dto.vehicle_model,
    vehiclePlate:
      pickString(record, "vehicle_plate", "vehiclePlate") || dto.vehicle_plate,
    experienceYears:
      pickNumber(record, "experience_years", "experienceYears") ||
      dto.experience_years,
    company: pickString(record, "company") || dto.company,
    enabled: Boolean(record["enabled"] ?? dto.enabled),
  };
}

export function slugHelpServiceCode(title: string, fallback = "custom"): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
  return slug || fallback;
}

export function toHelpServiceWriteBody(input: {
  kind: HelpService["kind"];
  code: string;
  title: string;
  description: string;
  unitPrice: number;
  pricingMode: HelpPricingMode;
  unitLabel: string;
  enabled: boolean;
  comingSoon: boolean;
  providerServiceId: string;
}): Record<string, unknown> {
  const providerId = input.providerServiceId.trim();
  return {
    kind: input.kind,
    code: input.code,
    title: input.title.trim(),
    description: input.description.trim(),
    unit_price: input.unitPrice,
    pricing_mode: input.pricingMode,
    unit_label: input.unitLabel.trim() || "заказ",
    enabled: input.enabled,
    coming_soon: input.comingSoon,
    provider_service_id: providerId || null,
  };
}

export function toItemPatch(item: HelpLineItem): HelpOrderItemPatchDto {
  return {
    service_uuid: item.serviceUuid ?? undefined,
    code: item.code || slugHelpServiceCode(item.title),
    title: item.title,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    pricing_mode: item.pricingMode,
  };
}

export function toPointDto(point: HelpPoint): HelpPointDto {
  return {
    city: point.city,
    address: point.address,
    lat: point.lat,
    lon: point.lon,
    note: point.note,
  };
}

export const MAX_EXECUTOR_EXPERIENCE_YEARS = 60;

/** Бэкенд принимает lat/lon в HelpOrderExecutorPatchDto. */
export const EXECUTOR_GEO_PATCH_ENABLED = true;

export function clampExperienceYears(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(
    MAX_EXECUTOR_EXPERIENCE_YEARS,
    Math.max(0, Math.round(value)),
  );
}

function clampCoord(
  value: number | null,
  min: number,
  max: number,
): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.min(max, Math.max(min, value));
}

export function clampExecutorLat(lat: number | null): number | null {
  return clampCoord(lat, -90, 90);
}

export function clampExecutorLon(lon: number | null): number | null {
  return clampCoord(lon, -180, 180);
}

export function executorPatchSnapshot(executor: HelpExecutor | null): string {
  if (!executor?.name.trim()) return "null";
  return JSON.stringify({
    source: executor.source,
    catalogUuid: executor.catalogUuid,
    name: executor.name.trim(),
    phone: executor.phone,
    vehicleModel: executor.vehicleModel,
    vehiclePlate: executor.vehiclePlate,
    experienceYears: clampExperienceYears(executor.experienceYears),
    company: executor.company,
    etaMinutes: executor.etaMinutes,
    lat: clampExecutorLat(executor.lat),
    lon: clampExecutorLon(executor.lon),
  });
}

export function toExecutorPatch(executor: HelpExecutor | null): UpdateHelpOrderDto["executor"] {
  if (!executor?.name.trim()) return null;
  const patch: Record<string, unknown> = {
    name: executor.name.trim(),
    phone: executor.phone,
    vehicle_model: executor.vehicleModel,
    vehicle_plate: executor.vehiclePlate,
    experience_years: clampExperienceYears(executor.experienceYears),
    company: executor.company,
    catalog_uuid: executor.catalogUuid || null,
    eta_minutes: executor.etaMinutes,
  };
  if (EXECUTOR_GEO_PATCH_ENABLED) {
    const lat = clampExecutorLat(executor.lat);
    const lon = clampExecutorLon(executor.lon);
    if (lat != null && lon != null) {
      patch["lat"] = lat;
      patch["lon"] = lon;
    } else {
      patch["lat"] = null;
      patch["lon"] = null;
    }
  }
  return patch as UpdateHelpOrderDto["executor"];
}

export function deriveBlockedWheels(items: HelpLineItem[]): number {
  const item = items.find((line) => line.code === "blocked_wheels");
  return item ? Math.max(0, Math.min(4, item.quantity)) : 0;
}

export function deriveHardToAccess(items: HelpLineItem[]): boolean {
  return items.some((line) => line.code === "hard_access");
}
